import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Film, Plus, Loader2, Upload } from "lucide-react";
import { uploadVideoFile, VideoStatusPill } from "./VideoReviewer";

export function TaskVideos({ taskId, apiClient = api, onLeave }) {
  const nav = useNavigate();
  const [videos, setVideos] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [pct, setPct] = useState(null);
  const fileRef = useRef(null);
  useEffect(() => { apiClient.get(`/console/tasks/${taskId}/videos`).then(({ data }) => setVideos(data.videos)).catch(() => setVideos([])); }, [taskId]); // eslint-disable-line
  const create = async () => {
    if (file.size > 500 * 1024 * 1024) { toast.error("Videos must be 500 MB or smaller"); return; }
    setPct(0);
    try {
      const { data: v } = await apiClient.post(`/console/tasks/${taskId}/videos`, { name: name.trim() });
      await uploadVideoFile(v.id, file, setPct, apiClient);
      toast.success("Video uploaded"); onLeave?.(); setTimeout(() => nav(`/console/videos/${v.id}`), 0);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); setPct(null); }
  };
  return (
    <div data-testid="task-videos-section">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-mono-data uppercase tracking-wider text-[#9FA1A4]">Videos</p>
        <button data-testid="task-add-video" onClick={() => { setName(""); setFile(null); setPct(null); setOpen(true); }} className="text-xs text-[#723FFF] hover:text-[#5b2fd6] flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Upload video</button>
      </div>
      {videos === null ? <Loader2 className="h-4 w-4 animate-spin text-[#c4c5cc]" /> : videos.length === 0 ? <p className="text-xs text-[#c4c5cc]">No videos yet.</p> : (
        <div className="space-y-1.5">
          {videos.map((v) => (
            <button key={v.id} data-testid={`task-video-${v.id}`} onClick={() => { onLeave?.(); setTimeout(() => nav(`/console/videos/${v.id}`), 0); }} className="w-full flex items-center gap-2.5 rounded-xl border border-[#EEEFEF] bg-white px-3 py-2 text-left hover:border-[#C9B8FF] transition-colors">
              <Film className="h-4 w-4 text-[#723FFF] shrink-0" />
              <span className="text-sm text-[#06060C] truncate flex-1"><span className="font-mono-data text-[11px] text-[#9FA1A4] mr-1.5">{v.code}</span>{v.name}</span>
              <span className="font-mono-data text-[10px] text-[#9FA1A4]">v{v.current_version || 0}</span>
              <VideoStatusPill status={v.status} />
            </button>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={(o) => !o && pct === null && setOpen(false)}>
        <DialogContent className="bg-white max-w-md" data-testid="task-video-dialog">
          <DialogHeader><DialogTitle className="font-display">Upload a video</DialogTitle><DialogDescription>Up to 500 MB. You can upload new versions later.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="task-video-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Video name, e.g. Spring promo 30s" className="bg-[#F6F6FD] border-[#EEEFEF]" />
            <input ref={fileRef} type="file" accept="video/*" hidden data-testid="task-video-file" onChange={(e) => { const f = e.target.files?.[0]; setFile(f || null); if (f && !name.trim()) setName(f.name.replace(/\.[^.]+$/, "")); }} />
            <button onClick={() => fileRef.current?.click()} disabled={pct !== null} className="w-full rounded-xl border border-dashed border-[#C9B8FF] bg-[#F8F5FF] py-5 text-sm text-[#5b2fd6] flex items-center justify-center gap-2"><Upload className="h-4 w-4" />{file ? `${file.name} · ${(file.size / 1048576).toFixed(1)} MB` : "Choose video file"}</button>
            {pct !== null && <div className="h-2 rounded-full bg-[#F0F0F5] overflow-hidden" data-testid="task-video-progress"><div className="h-full bg-[#723FFF] transition-[width]" style={{ width: `${pct}%` }} /></div>}
          </div>
          <DialogFooter><Button data-testid="task-video-upload" onClick={create} disabled={!file || !name.trim() || pct !== null} className="rounded-full bg-[#06060C] text-white hover:bg-[#1c1c24]">{pct !== null ? `Uploading ${pct}%` : "Upload"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
